import { create } from "zustand";
import { spendStatus, type AssistantProviderId } from "@ide/shared/assistant";
import { monthKey, monthTotal, pruneMonths, useAiUsage } from "@/lib/ai-usage";

/**
 * Limite de gasto do agente, por conta de IA (cada provedor conectado neste computador) e por mês,
 * em US$. O gasto é estimado pela tabela de preços (`assistantCost`).
 *
 * A Anthropic usa o mesmo orçamento e o mesmo gasto do cartão "Uso de IA" (o agente já registra lá
 * como "claude"), para a conta ter um limite só. Os outros provedores ficam aqui.
 */

const KEY = "kompil.agent.spend";

type Limits = Partial<Record<AssistantProviderId, number>>;
type Months = Record<string, Partial<Record<AssistantProviderId, number>>>;

interface Saved {
  limits: Limits;
  months: Months;
  /** Aviso de 80% já dado ("2026-10|openai"). */
  warned: string[];
}

function read(): Saved {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? "null") as Partial<Saved> | null;
    return {
      limits: raw?.limits && typeof raw.limits === "object" ? raw.limits : {},
      months: raw?.months && typeof raw.months === "object" ? raw.months : {},
      warned: Array.isArray(raw?.warned) ? raw.warned.filter((w) => typeof w === "string").slice(-50) : [],
    };
  } catch {
    return { limits: {}, months: {}, warned: [] };
  }
}

function write(s: Saved) {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    // sem armazenamento: vale só nesta sessão
  }
}

interface SpendState extends Saved {
  setLimit(provider: AssistantProviderId, usd: number | null): void;
  add(provider: AssistantProviderId, usd: number): void;
  /** Marca o aviso de 80% do mês como dado; devolve false se já tinha sido. */
  warnOnce(provider: AssistantProviderId): boolean;
}

export const useAgentSpend = create<SpendState>((set, get) => {
  const save = (patch: Partial<Saved>) => {
    set(patch);
    const { limits, months, warned } = get();
    write({ limits, months, warned });
  };
  return {
    ...read(),
    setLimit: (provider, usd) => {
      const value = usd && Number.isFinite(usd) && usd > 0 ? Math.round(usd * 100) / 100 : null;
      if (provider === "anthropic") return useAiUsage.getState().setClaudeBudget(value);
      const limits = { ...get().limits };
      if (value) limits[provider] = value;
      else delete limits[provider];
      save({ limits });
    },
    add: (provider, usd) => {
      if (provider === "anthropic" || !(usd > 0)) return;
      const month = monthKey();
      const current = get().months[month] ?? {};
      const months = pruneMonths({
        ...get().months,
        [month]: { ...current, [provider]: (current[provider] ?? 0) + usd },
      });
      save({ months });
    },
    warnOnce: (provider) => {
      const key = `${monthKey()}|${provider}`;
      if (get().warned.includes(key)) return false;
      save({ warned: [...get().warned, key].slice(-50) });
      return true;
    },
  };
});

/** Gasto do mês e limite de uma conta (fora de React). */
export function spendOf(provider: AssistantProviderId) {
  if (provider === "anthropic") {
    const usage = useAiUsage.getState();
    return { spent: monthTotal(usage.months, ["claude"]).cost, limit: usage.claudeBudgetUsd };
  }
  const s = useAgentSpend.getState();
  return { spent: s.months[monthKey()]?.[provider] ?? 0, limit: s.limits[provider] ?? null };
}

/** O mesmo, reagindo às mudanças (para a interface). */
export function useSpend(provider: AssistantProviderId | null) {
  useAgentSpend((s) => s.months);
  useAgentSpend((s) => s.limits);
  useAiUsage((s) => s.months);
  useAiUsage((s) => s.claudeBudgetUsd);
  if (!provider) return { spent: 0, limit: null, status: "free" as const };
  const { spent, limit } = spendOf(provider);
  return { spent, limit, status: spendStatus(spent, limit) };
}

/** Valores rápidos para o limite (US$ por mês). */
export const SPEND_PRESETS = [5, 10, 20, 50, 100] as const;

const usd = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "USD", minimumFractionDigits: 2 });
/** "US$ 3,10" (menos de um centavo: "< US$ 0,01"). */
export const formatSpend = (n: number) => (n > 0 && n < 0.01 ? `< ${usd.format(0.01)}` : usd.format(n));

/** Chave da lista de modelos de um provedor (a mesma no cabeçalho do agente e no envio). */
export const modelsKey = (provider: AssistantProviderId | null) => ["assistant", "models", provider] as const;

/** Preços informados pelo provedor (OpenRouter), guardados quando a lista de modelos chega. */
const knownPrices = new Map<string, { input: number; output: number }>();

export function rememberPrices(
  provider: AssistantProviderId,
  prices: Record<string, { input: number; output: number }>,
) {
  for (const [model, price] of Object.entries(prices)) knownPrices.set(`${provider}|${model}`, price);
}

export const knownPrice = (provider: AssistantProviderId, model: string) =>
  knownPrices.get(`${provider}|${model}`);
